import { CrewMerchantUi } from 'corso-types';
import { z } from 'zod';
import MonetaryRateInput, {
  MonetaryType,
  MonetaryValue,
} from '~/components/field/MonetaryRateInput';

// Derive the union from the schema so this component flexes when the schema
// shape changes (e.g. a future third value kind).
type DiscountValue = z.infer<
  typeof CrewMerchantUi.discountDefinitionSchema
>['parameters']['customerGets']['value'];

// MonetaryRateInput stores percent as a decimal (0.10 → "10%"); the discount
// schema stores it as a whole number (10). Fixed amounts are strings in the
// schema (Shopify's money format) but numbers in the input.
const toMonetaryValue = (value: DiscountValue | undefined): MonetaryValue => {
  if (value && 'amount' in value) {
    return { type: MonetaryType.fixed, amount: Number(value.amount) || 0 };
  }
  return {
    type: MonetaryType.percent,
    amount: value ? value.percentage / 100 : 0,
  };
};

const toDiscountValue = (
  next: MonetaryValue,
  prev: DiscountValue | undefined,
): DiscountValue => {
  if (next.type === MonetaryType.percent) {
    return { percentage: next.amount * 100 };
  }
  return {
    amount: next.amount.toFixed(2),
    appliesOnEachItem:
      prev && 'amount' in prev ? prev.appliesOnEachItem : false,
  };
};

/**
 * Pure component for the discount-value kind toggle + value input.
 *
 * The parent wraps this in a single `<Controller>` over the
 * `customerGets.value` path so no generic typing is needed here.
 *
 * Note: `appliesOnEachItem` defaults to `false` when flipping to the
 * fixed-amount variant. Callers that need to toggle it (e.g. the product
 * form's "Only apply discount once per order" checkbox) bind their own
 * `<Controller>` to the same `value` path alongside this one.
 */
export function DiscountValueField({
  value,
  onChange,
  currencyCode,
  currencySymbol,
  error,
}: {
  value: DiscountValue | undefined;
  onChange: (next: DiscountValue) => void;
  currencyCode: string;
  currencySymbol: string;
  error?: string;
}) {
  return (
    <MonetaryRateInput
      id="discount-value"
      label="Discount value"
      currencyCode={currencyCode}
      currencySymbol={currencySymbol}
      value={toMonetaryValue(value)}
      onChange={(next) => onChange(toDiscountValue(next, value))}
      error={error}
    />
  );
}
